import { Injectable, OnDestroy } from "@angular/core";
import { CubiqWorker } from "../worker/CubiqWorker";
import { SystemConfigPromiseService } from "shared/auth-services";
import { SysFeatures } from "../model/core/SysFeatures";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class HeartBeatMonitor implements OnDestroy {
  private lastHeartBeat: Date = new Date();
  private timeoutSeconds: number = 120;
  private pingInterval: number = 30;
  private intervalId = null;

  constructor(
    private worker: CubiqWorker,
    private systemConfigService: SystemConfigPromiseService,
  ) {
    this.worker.isAuth().then(
      () => {
        this.initialize();
      },
      () => {
        logger.out.info(`${this.constructor.name}() skipping initialization`);
      },
    );
  }

  ngOnDestroy(): void {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
    }
  }

  protected initialize(): void {
    logger.out.info(`${this.constructor.name}.initialize()`);

    this.systemConfigService
      .getSystemConfig(SysFeatures.streamingPingTimeout)
      .then((value: string) => {
        if (value != null) {
          try {
            this.timeoutSeconds = parseInt(value);
          } catch (error) {
            logger.out.info(
              "Invalid value for " +
                SysFeatures.streamingPingTimeout +
                ". Should be number of seconds.",
            );
          }
        }
      })
      .catch((error: any) => {
        logger.out.warn(
          "Failed to load timeout system value " +
            SysFeatures.streamingPingTimeout +
            ". Using default value of " +
            this.timeoutSeconds +
            " seconds.",
        );
      });

    this.systemConfigService
      .getSystemConfig("USER_PING_INTERVAL_UI_SEC")
      .then((value: string) => {
        if (value != null) {
          try {
            this.pingInterval = parseInt(value);
          } catch (error) {
            logger.out.info(
              "Invalid value for " +
                SysFeatures.streamingPingTimeout +
                ". Should be number of seconds.",
            );
          }
        }
        this.intervalId = setInterval(() => {
          this.sendPing();
        }, this.pingInterval * 1000);
      })
      .catch((error: any) => {
        logger.out.warn(
          "Failed to load timeout system value " +
            "USER_PING_INTERVAL_UI_SEC" +
            ". Using default value of " +
            this.pingInterval +
            " seconds.",
        );
        this.intervalId = setInterval(this.sendPing.bind(this), this.pingInterval * 1000);
      });

    this.worker
      .getEncodableObservable<CubiqTS.PingTimerImpl>(CubiqTS.PingTimerImpl.name)
      .subscribe({
        next: (response) => {
          // only for serverIsProxy:true case
          this.processResponse(response);
        },
      });
  }

  protected checkTimeout(): void {
    let expired: boolean =
      new Date().getTime() - this.lastHeartBeat.getTime() > this.timeoutSeconds * 1000;

    if (expired) {
      logger.out.error(
        `${this.constructor.name}.checkTimeout() missed heartbeat after ${this.timeoutSeconds} seconds. would disconnect`,
      );
    } else {
      logger.out.info(`${this.constructor.name}.checkTimeout() heartbeat ok`);
      this.lastHeartBeat = new Date();
    }
  }

  protected processResponse(response: CubiqTS.PingTimerImpl): void {
    if (response != null && response instanceof CubiqTS.PingTimerImpl) {
      this.lastHeartBeat = new Date();
      let now = CubiqTS.Long.fromString(`${new Date().getTime()}`);
      logger.out.info(
        `${this.constructor.name} Server Ping Diff: ${response.serverTxTime
          .subtract(response.serverRxTime)
          .toString()}ms`,
      );
      logger.out.info(
        `${this.constructor.name} Broker Ping Diff: ${response.svrTxTime
          .subtract(response.svrRxTime)
          .toString()}ms`,
      );
      logger.out.info(
        `${this.constructor.name} UI Ping Diff: ${now.subtract(response.uiTxTime).toString()}ms`,
      );
      logger.out.info(
        `${this.constructor.name} UI Start vs Server Start: ${response.serverRxTime
          .subtract(response.uiTxTime)
          .toString()}ms`,
      );
      logger.out.info(
        `${this.constructor.name} UI Start vs Proxy Start: ${response.svrRxTime
          .subtract(response.uiTxTime)
          .toString()}ms`,
      );
      logger.out.info(
        `${this.constructor.name} Proxy End vs UI End: ` +
          `${now.subtract(response.svrTxTime).toString()}ms`,
      );
    } else {
      logger.out.error(`${this.constructor.name} %o`, response);
      logger.out.error(`${this.constructor.name} would disconnect`);
    }
  }

  public sendPing(): void {
    let uiTxTime = new Date().getTime();
    let ping = new CubiqTS.PingTimerImpl();
    ping.uiTxTime = CubiqTS.Long.fromString(`${uiTxTime}`);
    ping.serverIsProxy = false;
    this.worker.postServiceRequest(ping).then(
      (response) => {
        if (response) {
          this.processResponse(response);
        }
      },
      (reason) => {
        logger.out.error(`${this.constructor.name} %o`, reason);
        logger.out.error(`${this.constructor.name} would disconnect`);
      },
    );

    this.checkTimeout();
  }
}
